'use client'

import { useState } from 'react'
import { FolderOpen, Download, FileSpreadsheet, Loader2, Clock } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  DashboardData,
  DashboardDocument,
  DocModule,
  DOC_MODULE_ORDER,
  MODULE_META,
  formatDate,
} from '../types'

interface Props {
  data: DashboardData
}

export default function DocumentsSection({ data }: Props) {
  const [busyId, setBusyId] = useState<string | null>(null)

  const exportWord = async (doc: DashboardDocument, mod: DocModule) => {
    if (!doc.content) {
      toast.error('Contenu indisponible pour ce document.')
      return
    }
    setBusyId(`word-${doc.id}`)
    try {
      const { exportToWord } = await import('@/lib/export-word')
      await exportToWord({
        text: doc.content,
        title: doc.title,
        filename: `qualyse_${MODULE_META[mod].label}_${doc.title.replace(/\s+/g, '_')}_${doc.created_at.slice(0, 10)}.docx`,
      })
      toast.success('Document Word téléchargé.')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erreur export Word.')
    } finally {
      setBusyId(null)
    }
  }

  const exportExcel = async (doc: DashboardDocument) => {
    if (!doc.content) {
      toast.error('Contenu indisponible pour ce document.')
      return
    }
    setBusyId(`xls-${doc.id}`)
    try {
      const { exportToExcel } = await import('@/lib/export-excel')
      await exportToExcel({
        text: doc.content,
        verbatimLabels: [],
        filename: `qualyse_codification_${doc.title.replace(/\s+/g, '_')}_${doc.created_at.slice(0, 10)}.xlsx`,
      })
      toast.success('Fichier Excel téléchargé.')
    } catch (err) {
      toast.error(
        err instanceof Error
          ? `Excel indisponible pour ce document (${err.message}). Essayez le format Word.`
          : 'Erreur export Excel.',
      )
    } finally {
      setBusyId(null)
    }
  }

  const totalDocs = DOC_MODULE_ORDER.reduce((s, m) => s + (data.documents[m]?.length ?? 0), 0)

  if (totalDocs === 0) {
    return (
      <div className="text-center py-12">
        <FolderOpen className="h-10 w-10 text-tq-muted mx-auto mb-3 opacity-40" />
        <p className="text-sm text-tq-muted">Aucun document sauvegardé.</p>
        <p className="text-xs text-tq-muted mt-1">
          Vos résultats apparaîtront ici après votre première utilisation.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {DOC_MODULE_ORDER.map((mod) => {
        const docs = data.documents[mod] ?? []
        const meta = MODULE_META[mod]
        return (
          <div key={mod}>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-base">{meta.emoji}</span>
              <p className="text-xs font-semibold text-tq-text uppercase tracking-wider">{meta.label}</p>
              <span className="text-xs text-tq-muted">({docs.length})</span>
            </div>

            {docs.length === 0 ? (
              <p className="text-xs text-tq-muted rounded-lg border border-dashed border-tq-border px-4 py-3">
                Aucun document pour ce module.
              </p>
            ) : (
              <div className="space-y-2">
                {docs.map((doc) => (
                  <div key={doc.id} className="rounded-lg border border-tq-border bg-white p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-tq-text truncate">{doc.title}</p>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className="text-xs text-tq-muted">
                            <Clock className="inline h-3 w-3 mr-0.5" />
                            {formatDate(doc.created_at)}
                          </span>
                          {doc.word_count != null && (
                            <span className="text-xs text-tq-muted">{doc.word_count} mots</span>
                          )}
                          {doc.statut && (
                            <span className="text-xs px-1.5 py-0.5 rounded-full bg-tq-bg text-tq-muted capitalize">
                              {doc.statut}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={!doc.content || busyId === `word-${doc.id}`}
                          onClick={() => exportWord(doc, mod)}
                          className="gap-1.5 border-tq-green/40 text-tq-green hover:bg-tq-green-lighter/50 text-xs"
                        >
                          {busyId === `word-${doc.id}` ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Download className="h-3.5 w-3.5" />
                          )}
                          Word
                        </Button>
                        {mod === 'codification' && (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={!doc.content || busyId === `xls-${doc.id}`}
                            onClick={() => exportExcel(doc)}
                            className="gap-1.5 border-tq-green/40 text-tq-green hover:bg-tq-green-lighter/50 text-xs"
                          >
                            {busyId === `xls-${doc.id}` ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <FileSpreadsheet className="h-3.5 w-3.5" />
                            )}
                            Excel
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
